import React, { useState } from 'react';
import { safeFetch } from '../../../App';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input, Textarea, Label } from '../../ui/Input';

const DEFAULT_FORM = {
    shareProfileImage: true,
    shareEntities: true,
    personalNote: '',
    sharePhone: false,
    phoneNumber: '',
    shareWhatsapp: false,
    shareEmail: false,
    email: '',
};

/**
 * Lets a logged-in user opt into being listed as a community member with
 * configurable visibility for each piece of personal info.
 *
 * Owns its own form state — callers just pass `chatId`, `zone`, and an
 * `onShared` hook that fires after a successful POST so the parent can
 * refresh the member list.
 */
export function ShareProfileModal({ isOpen, onClose, chatId, zone, onShared }) {
    const [form, setForm] = useState(DEFAULT_FORM);
    const [submitting, setSubmitting] = useState(false);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/share-user-with-zone',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: chatId,
                        zone_id: zone,
                        share_name: true,
                        share_profile_image: form.shareProfileImage,
                        share_entities: form.shareEntities,
                        personal_note: form.personalNote,
                        share_phone: form.sharePhone,
                        phone_number: form.phoneNumber,
                        share_whatsapp: form.shareWhatsapp,
                        share_email: form.shareEmail,
                        email: form.email,
                    }),
                },
            );
            if (!res.ok) {
                const txt = await res.text();
                console.error('Share profile failed:', txt);
                alert('שיתוף הפרופיל נכשל');
                return;
            }
            alert('הפרופיל שלך שותף בהצלחה');
            onShared?.();
            onClose();
        } catch (err) {
            console.error('Share profile error:', err);
            alert('שגיאה בשיתוף הפרופיל');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="שתפו את הפרופיל שלכם"
            subtitle="בחרו מה תרצו לשתף עם חברי הקהילה באזור"
            size="md"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={submitting}>ביטול</Button>
                    <Button variant="primary" onClick={handleSubmit} loading={submitting}>
                        שתפו פרופיל
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                <CheckboxRow
                    title="שם משתמש"
                    description="השם שלך ישותף תמיד (שדה חובה)"
                    checked
                    disabled
                />
                <CheckboxRow
                    title="תמונת פרופיל"
                    description="הצג את תמונת הפרופיל שלך"
                    checked={form.shareProfileImage}
                    onChange={(v) => update({ shareProfileImage: v })}
                />
                <CheckboxRow
                    title="העסקים/מפגשים שלי"
                    description="הצג את הישויות שיצרת באזור"
                    checked={form.shareEntities}
                    onChange={(v) => update({ shareEntities: v })}
                />

                <div className="space-y-1.5">
                    <Label>הערה אישית (אופציונלי)</Label>
                    <Textarea
                        value={form.personalNote}
                        onChange={(e) => update({ personalNote: e.target.value })}
                        placeholder="כתבו משהו על עצמכם שחברי הקהילה יראו..."
                        rows={4}
                    />
                </div>

                <div className="border-t border-line-subtle pt-4 space-y-3">
                    <h4 className="font-bold text-ink text-base">פרטי התקשרות</h4>

                    <CheckboxRow
                        title="טלפון"
                        description="שתפו את מספר הטלפון שלכם"
                        checked={form.sharePhone}
                        onChange={(v) => update({ sharePhone: v })}
                    />
                    {form.sharePhone && (
                        <Input
                            type="tel"
                            value={form.phoneNumber}
                            onChange={(e) => update({ phoneNumber: e.target.value })}
                            placeholder="05X-XXX-XXXX"
                        />
                    )}

                    <CheckboxRow
                        title="זמין בוואטסאפ"
                        description="ניתן ליצור קשר דרך WhatsApp"
                        checked={form.shareWhatsapp}
                        onChange={(v) => update({ shareWhatsapp: v })}
                    />

                    <CheckboxRow
                        title="אימייל"
                        description="שתפו את כתובת המייל שלכם"
                        checked={form.shareEmail}
                        onChange={(v) => update({ shareEmail: v })}
                    />
                    {form.shareEmail && (
                        <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => update({ email: e.target.value })}
                            placeholder="example@mail.com"
                        />
                    )}
                </div>
            </div>
        </Modal>
    );
}

function CheckboxRow({ title, description, checked, disabled = false, onChange }) {
    return (
        <label
            className={[
                'flex items-center gap-3 p-4 rounded-lg border transition-colors',
                disabled
                    ? 'bg-surface-muted border-line-subtle opacity-75 cursor-not-allowed'
                    : 'bg-surface-muted border-line-subtle hover:bg-surface-sunken cursor-pointer',
            ].join(' ')}
        >
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => !disabled && onChange?.(e.target.checked)}
                className="w-5 h-5 rounded border-2 border-brand-500 text-brand-500 focus:ring-2 focus:ring-brand-400/30"
            />
            <div className="flex-1">
                <div className="font-bold text-ink">{title}</div>
                <div className="text-sm text-ink-muted">{description}</div>
            </div>
        </label>
    );
}

export default ShareProfileModal;
